// src/Pages/Trabajador/seguimiento.jsx
import React, { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../../firebase/config";
import { useAuth } from "../../context/AuthContext";
import { Loader2, AlertCircle, Users } from "lucide-react";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function parseReservaDate(r) {
  if (!r || !r.fecha) return null;
  try {
    const [Y, M, D] = String(r.fecha).split("-").map(Number);
    const d = new Date(Y || 1970, (M || 1) - 1, D || 1);

    if (typeof r.hora === "string" && /^\d{1,2}:\d{2}$/.test(r.hora)) {
      const [H, Mm] = r.hora.split(":").map(Number);
      d.setHours(H || 0, Mm || 0, 0, 0);
    }

    return d;
  } catch {
    return null;
  }
}

function isFutureReserva(r) {
  const d = parseReservaDate(r);
  if (!d) return false;
  const now = new Date();
  return d.getTime() > now.getTime();
}

function fmtFechaCorta(date) {
  if (!date || Number.isNaN(date.getTime?.())) return "—";
  try {
    return date.toLocaleDateString("es-CL", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  } catch {
    return "—";
  }
}

export default function TrabajadorSeguimiento() {
  const { user } = useAuth();
  const [reservas, setReservas] = useState([]);
  const [pacientes, setPacientes] = useState({});
  const [loading, setLoading] = useState(true);
  const [thresholdDays, setThresholdDays] = useState(1); // umbral de días

  const [planes, setPlanes] = useState([]);


// 1) Planes de usuarios (todos los planes; worker puede leerlos)
useEffect(() => {
  if (!user?.uid) return;
  setLoading(true);

  const unsub = onSnapshot(
    collection(db, "planes_usuarios"),
    (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setPlanes(items);
      setLoading(false);
    },
    (e) => {
      console.error("Error cargando planes_usuarios", e);
      setLoading(false);
    }
  );

  return () => unsub();
}, [user?.uid]);

// 2) Reservas del trabajador (para saber última sesión y si tiene próximas)
useEffect(() => {
  if (!user?.uid) return;

  const q = query(
    collection(db, "reservas"),
    where("profesionalId", "==", user.uid)
  );

  const unsub = onSnapshot(
    q,
    (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setReservas(items);
    },
    (e) => {
      console.error("Error cargando reservas del trabajador", e);
    }
  );

  return () => unsub();
}, [user?.uid]);

// 3) Mapeamos datos básicos de pacientes desde reservas (como antes)
useEffect(() => {
  const acc = {};

  reservas.forEach((r) => {
    const uid = r.uid;
    if (!uid) return;
    if (acc[uid]) return;

    acc[uid] = {
      id: uid,
      nombre:
        r.nombrePaciente ||
        r.nombresPaciente ||
        r.nombres ||
        r.nombre ||
        r.datosPaciente?.nombres ||
        "",
      apellido:
        r.apellidosPaciente ||
        r.apellidoPaciente ||
        r.apellidos ||
        r.apellido ||
        r.datosPaciente?.apellidos ||
        "",
      correo:
        r.correoPaciente ||
        r.emailPaciente ||
        r.correo ||
        r.email ||
        r.datosPaciente?.correo ||
        "",
      telefono:
        r.telefonoPaciente ||
        r.telefono ||
        r.datosPaciente?.telefono ||
        "",
    };
  });

  setPacientes(acc);
}, [reservas]);

// 4) Construimos lista de seguimiento combinando planes + reservas (1 fila por paciente)
const lista = useMemo(() => {
  if (!planes.length) return [];
  const now = new Date();

  // Agrupamos reservas por uid para calcular última sesión y futuras
  const reservasByUid = new Map();
  reservas.forEach((r) => {
    const uid = r.uid;
    if (!uid) return;
    const arr = reservasByUid.get(uid) || [];
    arr.push(r);
    reservasByUid.set(uid, arr);
  });

  // Usamos un mapa por uid para agrupar planes de un mismo paciente
  const porUid = new Map();

  planes.forEach((plan) => {
    const uid = plan.uid; // en planes_usuarios el uid del paciente
    if (!uid) return;

    const resPaciente = reservasByUid.get(uid) || [];

    // Reservas pasadas (para última sesión)
    const past = resPaciente.filter((r) => !isFutureReserva(r));
    let lastDate = null;
    past.forEach((r) => {
      const d = parseReservaDate(r);
      if (!d) return;
      if (!lastDate || d > lastDate) lastDate = d;
    });

    // Si nunca ha tenido sesión contigo, no entra a seguimiento
    if (!lastDate) return;

    const diffDays = Math.floor(
      (now.getTime() - lastDate.getTime()) / MS_PER_DAY
    );
    if (diffDays < thresholdDays) return;

    const info = pacientes[uid] || {};

    const sesionesTot = plan.sesiones_totales || 0;
    const sesionesUsadas = plan.sesiones_usadas || 0;

    const existente = porUid.get(uid);
    if (!existente) {
      porUid.set(uid, {
        uid,
        lastDate,
        diffDays,
        totalSesiones: sesionesTot,
        sesionesUsadas,
        sesionesRestantes: sesionesTot - sesionesUsadas,
        nombre: info.nombre || "",
        apellido: info.apellido || "",
        correo: info.correo || info.email || "",
        telefono: info.telefono || "",
      });
    } else {
      // Acumulamos info de planes adicionales del mismo paciente
      existente.totalSesiones += sesionesTot;
      existente.sesionesUsadas += sesionesUsadas;
      existente.sesionesRestantes =
        existente.totalSesiones - existente.sesionesUsadas;

      // Actualizamos última sesión / días si corresponde
      if (lastDate > existente.lastDate) {
        existente.lastDate = lastDate;
        existente.diffDays = diffDays;
      } else if (diffDays > existente.diffDays) {
        existente.diffDays = diffDays;
      }
    }
  });

  const out = Array.from(porUid.values());
  out.sort((a, b) => b.diffDays - a.diffDays);
  return out;
}, [planes, reservas, pacientes, thresholdDays]);



  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4 md:space-y-5">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-semibold">
            Seguimiento de pacientes
          </h1>
          <p className="text-xs md:text-sm text-gray-600 mt-1">
            Pacientes tuyos sin próxima sesión agendada y con varios días desde
            la última atención.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-xs sm:text-sm">
          <span className="text-gray-600">Umbral de días sin sesión:</span>
          <select
            className="border rounded-lg px-2 py-1 text-xs sm:text-sm bg-white"
            value={thresholdDays}
            onChange={(e) => setThresholdDays(Number(e.target.value) || 21)}
          >
            <option value={1}>1 día</option>
            <option value={7}>7 días</option>
            <option value={14}>14 días</option>
            <option value={21}>21 días</option>
            <option value={30}>30 días</option>
            <option value={45}>45 días</option>
          </select>
        </div>
      </div>

      {/* Estados generales */}
      {loading && (
        <div className="flex items-center gap-2 text-gray-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" />
          Cargando reservas…
        </div>
      )}

      {!loading && lista.length === 0 && (
        <div className="flex items-start gap-2 text-xs sm:text-sm text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            No hay pacientes que requieran seguimiento según el umbral
            seleccionado.
          </span>
        </div>
      )}

      {/* Lista de pacientes */}
      {!loading && lista.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <Users className="w-4 h-4" />
            <span>
              {lista.length} paciente{lista.length !== 1 ? "s" : ""} sin próxima
              sesión y con más de {thresholdDays} día
              {thresholdDays !== 1 ? "s" : ""} desde la última.
            </span>
          </div>

          {lista.map((p) => (
            <div
              key={p.uid}
              className="bg-white border rounded-xl shadow-sm p-3 sm:p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
            >
              {/* Datos de contacto */}
              <div className="min-w-0">
                <div className="font-medium text-sm sm:text-base truncate">
                  {p.nombre} {p.apellido}
                </div>
                {p.correo && (
                  <div className="text-xs sm:text-sm text-gray-600 break-all">
                    {p.correo}
                  </div>
                )}
                {p.telefono && (
                  <div className="text-xs sm:text-sm text-gray-600">
                    {p.telefono}
                  </div>
                )}
              </div>

              {/* Info de seguimiento */}
              <div className="text-xs sm:text-sm text-gray-700 space-y-1 md:text-right">
                <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 text-[11px] sm:text-xs">
                  <span className="font-semibold">{p.diffDays}</span>
                  <span>días sin sesión</span>
                </div>
                <div>
                  Última sesión:{" "}
                  <span className="font-medium">
                    {fmtFechaCorta(p.lastDate)}
                  </span>
                </div>
                <div>
                  Total de sesiones registradas:{" "}
                  <span className="font-medium">{p.totalSesiones}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
